import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths,
  addWeeks, subWeeks, addDays as addDaysFn, subDays, format, isSameMonth, isToday, getDay,
} from "date-fns";
import api from "../lib/api";
import { listTasks, createTask, updateTask } from "../lib/offlineTasks";
import { deleteCapture } from "../lib/offlineCaptures";
import { useIsMobile } from "../hooks/use-mobile";
import { ModuleNavBar } from "../components/ModuleNavBar";
import { TaskDetailDialog } from "../components/TaskDetailDialog";
import { CaptureEditDialog } from "../components/CapturePreviewDialog";
import { OverdueTasksCard, FavoriteNotesCard, DingtalkLinksCard } from "../components/TasksSidePanel";
import { GlobalNoteBox } from "../components/GlobalNoteBox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { CaretLeft, CaretRight, CalendarBlank, Plus, BellRinging, DotsSixVertical } from "@phosphor-icons/react";
import { useTaskReminders } from "../hooks/useTaskReminders";

const VIEW_MODES = ["month", "week", "day"];
const DEFAULT_HOUR_HEIGHT = 48; // px per hour on the day-view timeline, before the container is measured
const HOURS = Array.from({ length: 15 }, (_, i) => i + 8); // 08:00-22:00, per user request
const pad2 = (n) => String(n).padStart(2, "0");
const MAX_MOBILE_DOTS = 3;
// Mobile touch drag-to-reschedule tuning (native HTML5 drag events don't
// fire on touch devices, so this is a from-scratch long-press+pointer-move
// implementation that mirrors the desktop mouse-drag behavior above).
const TOUCH_LONG_PRESS_MS = 300;
const TOUCH_MOVE_CANCEL_PX = 10; // movement before long-press fires = scroll intent, not drag
const TOUCH_EDGE_SCROLL_ZONE_PX = 50;
const TOUCH_EDGE_SCROLL_STEP_PX = 12;
const TOUCH_SNAP_MINUTES = 30;

export default function SchedulePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tasks, setTasks] = useState([]);
  const [viewMode, setViewMode] = useState("month");
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [addDialogDate, setAddDialogDate] = useState(null); // "yyyy-MM-dd" | null
  const [newTitle, setNewTitle] = useState("");
  const [newDueTime, setNewDueTime] = useState("");
  const [newUnscheduledTitle, setNewUnscheduledTitle] = useState("");
  const [dragOverDate, setDragOverDate] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [daySheetDate, setDaySheetDate] = useState(null); // mobile month view: "yyyy-MM-dd" | null
  // Favorite-note editing, opened directly from the day view's "我的收藏"
  // widget (mirrors TasksPage's identical pattern) so the user never
  // leaves the Schedule page/URL for it.
  const [viewingCapture, setViewingCapture] = useState(null);
  const [favoritesRefreshKey, setFavoritesRefreshKey] = useState(0);
  const { overdueTasks, reminderCount } = useTaskReminders(tasks);
  const timelineRef = useRef(null);
  // Per user request, no view should ever need a scrollbar to see itself -
  // the day-view timeline's hour rows are the one exception that has a
  // truly fixed number of rows (HOURS.length), so instead of a fixed
  // per-hour pixel height, measure the timeline's actual available height
  // and divide it evenly so exactly 15 rows always fit with zero leftover
  // space and no scrollbar (desktop only - mobile keeps its own scroll).
  const [hourHeight, setHourHeight] = useState(DEFAULT_HOUR_HEIGHT);
  // Active mobile touch-drag session (null when nothing is being dragged).
  // { taskId, context: "timeline" | "grid", dx, dy, previewLabel, overDateKey }
  const [activeDrag, setActiveDrag] = useState(null);
  // Mutable bookkeeping for the in-progress gesture that doesn't need to
  // trigger a re-render on every touch event (unlike activeDrag above).
  const touchInfoRef = useRef({ longPressTimer: null, scrollInterval: null, startX: 0, startY: 0, scrollTopAtStart: 0, longPressed: false, cancelled: false });

  useEffect(() => {
    const el = timelineRef.current;
    if (!el || viewMode !== "day" || isMobile) return;
    const measure = () => {
      const available = el.clientHeight;
      if (available > 0) setHourHeight(Math.max(28, Math.floor(available / HOURS.length)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [viewMode, isMobile]);

  const loadTasks = async () => {
    const data = await listTasks();
    setTasks(data);
  };

  useEffect(() => {
    loadTasks();
  }, []);

  // Deep-link support for the global search dropdown - it navigates here
  // with ?openTask=<id>&date=<due_date> for scheduled tasks. Jumps the
  // calendar to that date immediately, then opens the task's detail dialog
  // once the task list has loaded.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openTaskId = params.get("openTask");
    const dateParam = params.get("date");
    if (!openTaskId) return;
    if (dateParam) setAnchorDate(new Date(`${dateParam}T00:00:00`));
    if (tasks.length === 0) return;
    const found = tasks.find((tk) => tk.id === openTaskId);
    if (found) setDetailTask(found);
    navigate("/schedule", { replace: true });
  }, [location.search, tasks]);

  const tasksByDate = useMemo(() => {
    const map = {};
    tasks.forEach((task) => {
      if (!task.due_date) return;
      (map[task.due_date] = map[task.due_date] || []).push(task);
    });
    // Chronological order within a day (08:00 before 13:30) - tasks came
    // in whatever order the /tasks list returned them in, not by due_time.
    // Untimed (all-day) tasks have no due_time to sort by, so they sort
    // first ahead of any timed task.
    Object.values(map).forEach((dayTasks) => {
      dayTasks.sort((a, b) => (a.due_time || "").localeCompare(b.due_time || ""));
    });
    return map;
  }, [tasks]);

  // Desktop week view only: tasks with no due_date yet, shown in a side
  // panel so the user can drag them straight onto a day cell to schedule
  // them (reuses the exact same native drag/drop mechanism as rescheduling
  // an already-placed task chip within the grid - handleDropTask just reads
  // a task id off the drop event regardless of where the drag started).
  const unscheduledTasks = useMemo(() => tasks.filter((tk) => !tk.due_date && !tk.completed), [tasks]);

  const handleQuickAddUnscheduled = async (e) => {
    e.preventDefault();
    if (!newUnscheduledTitle.trim()) return;
    try {
      const data = await createTask({ title: newUnscheduledTitle.trim() });
      setTasks((prev) => [data, ...prev]);
      setNewUnscheduledTitle("");
    } catch (e) {
      toast.error(t("tasks.createFailedError"));
    }
  };

  const days = useMemo(() => {
    let start;
    let end;
    if (viewMode === "month") {
      start = startOfWeek(startOfMonth(anchorDate));
      end = endOfWeek(endOfMonth(anchorDate));
    } else if (viewMode === "week") {
      start = startOfWeek(anchorDate, { weekStartsOn: 1 });
      end = endOfWeek(anchorDate, { weekStartsOn: 1 });
    } else {
      return [anchorDate];
    }
    const result = [];
    let cursor = start;
    while (cursor <= end) {
      result.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return result;
  }, [anchorDate, viewMode]);

  // Auto-scroll the day-view timeline so "now" is in view on open, instead of
  // making the user scroll through many empty early-morning hours first.
  // Grid starts at HOURS[0] (08:00), not midnight - offsets are relative to that.
  useEffect(() => {
    if (viewMode !== "day" || !timelineRef.current) return;
    const now = new Date();
    const gridStartMinutes = HOURS[0] * 60;
    const targetTop = isToday(anchorDate) ? (now.getHours() * 60 + now.getMinutes() - gridStartMinutes) * (hourHeight / 60) : 0;
    timelineRef.current.scrollTo({ top: Math.max(targetTop - hourHeight * 2, 0), behavior: "auto" });
  }, [viewMode, anchorDate, hourHeight]);

  const handleToggleComplete = async (task) => {
    const data = await updateTask(task.id, { completed: !task.completed });
    setTasks((prev) => prev.map((tk) => (tk.id === task.id ? data : tk)));
  };

  const handleOpenFavoriteCapture = async (captureId) => {
    try {
      const { data } = await api.get(`/captures/${captureId}`);
      setViewingCapture(data);
    } catch (e) {
      toast.error(t("common.genericError"));
    }
  };

  const handleViewingCaptureOpenChange = (isOpen) => {
    if (!isOpen) {
      setViewingCapture(null);
      setFavoritesRefreshKey((k) => k + 1);
    }
  };

  const handleViewingCaptureDeleted = async (captureId) => {
    try {
      await deleteCapture(captureId);
      toast.success(t("dashboard.movedToTrashSuccess"));
    } catch (e) {
      toast.error(t("common.genericError"));
    } finally {
      setViewingCapture(null);
      setFavoritesRefreshKey((k) => k + 1);
    }
  };

  // Drag-and-drop rescheduling (mouse only - month/week grid views on
  // desktop). Touch devices can't fire native HTML5 drag events, so on
  // mobile we don't render draggable chips at all; tapping a task opens
  // TaskDetailDialog instead, where the due date field is a touch-friendly
  // way to reschedule.
  const handleDropTask = async (taskId, newDate) => {
    setDragOverDate(null);
    const task = tasks.find((tk) => tk.id === taskId);
    if (!task || task.due_date === newDate) return;
    try {
      const data = await updateTask(taskId, { due_date: newDate });
      setTasks((prev) => prev.map((tk) => (tk.id === taskId ? data : tk)));
      toast.success(t("schedule.taskMovedSuccess", { date: newDate }));
    } catch (e) {
      toast.error(t("tasks.updateFailedError"));
    }
  };

  // Day-view timeline rescheduling (mouse only, mirrors handleDropTask
  // above) - dropping a timed task chip anywhere on the timeline computes
  // the new due_time from the drop's pixel position, snapped to the
  // nearest 30 minutes per user request. No overlap/conflict detection -
  // tasks are allowed to freely overlap in the timeline.
  const handleTimelineDrop = async (e) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("text/plain");
    const container = timelineRef.current;
    if (!taskId || !container) return;
    const rect = container.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + container.scrollTop;
    const gridStartMinutes = HOURS[0] * 60;
    const gridEndMinutes = (HOURS[HOURS.length - 1] + 1) * 60;
    let minutes = gridStartMinutes + (offsetY / hourHeight) * 60;
    minutes = Math.round(minutes / 30) * 30;
    minutes = Math.max(gridStartMinutes, Math.min(minutes, gridEndMinutes - 30));
    const newTime = `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
    const task = tasks.find((tk) => tk.id === taskId);
    if (!task || task.due_time === newTime) return;
    try {
      const data = await updateTask(taskId, { due_time: newTime });
      setTasks((prev) => prev.map((tk) => (tk.id === taskId ? data : tk)));
      toast.success(t("schedule.taskTimeMovedSuccess", { time: newTime }));
    } catch (e) {
      toast.error(t("tasks.updateFailedError"));
    }
  };

  const vibrate = (ms) => {
    try { if (navigator.vibrate) navigator.vibrate(ms); } catch {}
  };

  const clearLongPressTimer = () => {
    if (touchInfoRef.current.longPressTimer) {
      clearTimeout(touchInfoRef.current.longPressTimer);
      touchInfoRef.current.longPressTimer = null;
    }
  };

  const stopEdgeAutoScroll = () => {
    if (touchInfoRef.current.scrollInterval) {
      clearInterval(touchInfoRef.current.scrollInterval);
      touchInfoRef.current.scrollInterval = null;
    }
  };

  useEffect(() => () => {
    clearLongPressTimer();
    stopEdgeAutoScroll();
  }, []);

  // Shared by both the live drag preview and the actual drop - pixel Y
  // (viewport coords) -> minutes-from-midnight, snapped to the half hour.
  // Returns null if clientY isn't actually over the timeline at all (e.g.
  // still up in the all-day section, or past the bottom), which callers
  // treat as "not a valid drop here" instead of clamping to 08:00/22:30.
  const minutesFromClientY = (clientY) => {
    const container = timelineRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    if (clientY < rect.top || clientY > rect.bottom) return null;
    const offsetY = clientY - rect.top + container.scrollTop;
    const gridStartMinutes = HOURS[0] * 60;
    const gridEndMinutes = (HOURS[HOURS.length - 1] + 1) * 60;
    let minutes = gridStartMinutes + (offsetY / hourHeight) * 60;
    minutes = Math.round(minutes / TOUCH_SNAP_MINUTES) * TOUCH_SNAP_MINUTES;
    return Math.max(gridStartMinutes, Math.min(minutes, gridEndMinutes - TOUCH_SNAP_MINUTES));
  };

  const minutesToTimeStr = (minutes) => `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;

  // Week/month grid dragging: whatever day container is visually under the
  // finger right now. Uses a dedicated data-drop-date attribute (NOT the
  // data-testid, which both day containers AND unrelated descendants like
  // the month view's per-day dot wrapper share overlapping prefixes with)
  // so this can never resolve to the wrong element. The dragged chip/dot
  // itself is excluded via pointer-events:none in dragStyleFor below, so
  // elementFromPoint reports what's actually underneath it, not itself.
  const resolveDropDateKey = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    const dayEl = el?.closest("[data-drop-date]");
    return dayEl?.getAttribute("data-drop-date") || null;
  };

  // Recomputes the live drag preview (dy compensated for however much the
  // timeline has auto-scrolled since the drag started, so the chip doesn't
  // drift away from the finger; time label) from whatever the finger's
  // last known position was. Called both on pointermove and on every
  // auto-scroll interval tick (the finger can be perfectly still while
  // still in the edge zone, which must keep scrolling + updating).
  const refreshTimelineDrag = (clientX, clientY) => {
    const info = touchInfoRef.current;
    const container = timelineRef.current;
    const scrollDelta = container ? container.scrollTop - info.scrollTopAtStart : 0;
    const dx = clientX - info.startX;
    const dy = (clientY - info.startY) + scrollDelta;
    const minutes = minutesFromClientY(clientY);
    setActiveDrag((prev) => (prev ? { ...prev, dx, dy, previewLabel: minutes != null ? minutesToTimeStr(minutes) : null } : prev));
  };

  const updateEdgeAutoScroll = (clientX, clientY) => {
    const container = timelineRef.current;
    stopEdgeAutoScroll();
    if (!container) return;
    const rect = container.getBoundingClientRect();
    let direction = 0;
    if (clientY - rect.top < TOUCH_EDGE_SCROLL_ZONE_PX) direction = -1;
    else if (rect.bottom - clientY < TOUCH_EDGE_SCROLL_ZONE_PX) direction = 1;
    if (direction !== 0) {
      touchInfoRef.current.scrollInterval = setInterval(() => {
        container.scrollTop += direction * TOUCH_EDGE_SCROLL_STEP_PX;
        refreshTimelineDrag(clientX, clientY);
      }, 16);
    }
  };

  // Visual "lifted" transform applied to whichever chip/row/dot is
  // currently being touch-dragged (desktop mouse drag uses the browser's
  // own native drag image instead, so this never applies there).
  // pointer-events:none is essential, not cosmetic: without it,
  // document.elementFromPoint() in resolveDropDateKey/minutesFromClientY's
  // callers would keep hitting this translated element itself (since it's
  // rendered on top at the finger's position) instead of whatever day
  // cell/timeline slot is actually underneath.
  const dragStyleFor = (taskId) => {
    if (!activeDrag || activeDrag.taskId !== taskId) return {};
    return {
      transform: `translate(${activeDrag.dx}px, ${activeDrag.dy}px) scale(1.04)`,
      zIndex: 50,
      boxShadow: "0 10px 24px rgba(0,0,0,0.28)",
      pointerEvents: "none",
    };
  };

  const handleChipPointerDown = (e, task, context) => {
    const info = touchInfoRef.current;
    info.startX = e.clientX;
    info.startY = e.clientY;
    info.scrollTopAtStart = timelineRef.current?.scrollTop ?? 0;
    info.longPressed = false;
    info.cancelled = false;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    clearLongPressTimer();
    info.longPressTimer = setTimeout(() => {
      info.longPressTimer = null;
      info.longPressed = true;
      vibrate(10);
      const initialMinutes = minutesFromClientY(e.clientY);
      setActiveDrag({
        taskId: task.id,
        context,
        dx: 0,
        dy: 0,
        previewLabel: context === "timeline" ? (task.due_time || (initialMinutes != null ? minutesToTimeStr(initialMinutes) : null)) : null,
        overDateKey: context === "grid" ? task.due_date || null : null,
      });
    }, TOUCH_LONG_PRESS_MS);
  };

  const handleChipPointerMove = (e) => {
    const info = touchInfoRef.current;
    const dx = e.clientX - info.startX;
    const dy = e.clientY - info.startY;
    if (!info.longPressed) {
      if (Math.hypot(dx, dy) > TOUCH_MOVE_CANCEL_PX) {
        clearLongPressTimer();
        info.cancelled = true;
      }
      return;
    }
    e.preventDefault();
    if (activeDrag?.context === "timeline") {
      refreshTimelineDrag(e.clientX, e.clientY);
      updateEdgeAutoScroll(e.clientX, e.clientY);
    } else {
      const overDateKey = resolveDropDateKey(e.clientX, e.clientY);
      setActiveDrag((prev) => (prev ? { ...prev, dx, dy, overDateKey } : prev));
    }
  };

  // Set briefly after a real (long-pressed) drag resolves, so the ghost
  // "click" the browser fires right after on touch/mouse release doesn't
  // reach a native onClick sitting on an ancestor (e.g. the month view's
  // day cell <button>) and pop open something the user didn't tap for.
  // preventDefault() on pointerup alone only suppresses this for
  // touch-originated pointers, not mouse-originated ones.
  const justDraggedRef = useRef(false);
  const markJustDragged = () => {
    justDraggedRef.current = true;
    setTimeout(() => { justDraggedRef.current = false; }, 300);
  };

  const handleChipPointerUp = async (e, task, context, { onTap } = {}) => {
    const info = touchInfoRef.current;
    const wasLongPressed = info.longPressed;
    const wasCancelled = info.cancelled;
    clearLongPressTimer();
    stopEdgeAutoScroll();
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    info.longPressed = false;
    info.cancelled = false;

    if (wasCancelled) return; // mid-scroll release - do nothing
    if (!wasLongPressed) {
      if (onTap) onTap();
      return; // plain tap - let it fall through to any native onClick too (e.g. month dots -> open day sheet)
    }
    e.preventDefault(); // swallow the synthetic click a touch drag-drop would otherwise fire next
    markJustDragged();
    vibrate(12);
    setActiveDrag(null);
    if (context === "timeline") {
      const minutes = minutesFromClientY(e.clientY);
      if (minutes == null) return; // released outside the timeline bounds - cancel, don't clamp
      const newTime = minutesToTimeStr(minutes);
      if (task.due_time === newTime) return;
      try {
        const data = await updateTask(task.id, { due_time: newTime });
        setTasks((prev) => prev.map((tk) => (tk.id === task.id ? data : tk)));
        toast.success(t("schedule.taskTimeMovedSuccess", { time: newTime }));
      } catch {
        toast.error(t("tasks.updateFailedError"));
      }
    } else {
      const newDateKey = resolveDropDateKey(e.clientX, e.clientY);
      if (!newDateKey || task.due_date === newDateKey) return;
      try {
        const data = await updateTask(task.id, { due_date: newDateKey });
        setTasks((prev) => prev.map((tk) => (tk.id === task.id ? data : tk)));
        toast.success(t("schedule.taskMovedSuccess", { date: newDateKey }));
      } catch {
        toast.error(t("tasks.updateFailedError"));
      }
    }
  };

  const handleChipPointerCancel = (e) => {
    clearLongPressTimer();
    stopEdgeAutoScroll();
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    touchInfoRef.current.longPressed = false;
    touchInfoRef.current.cancelled = false;
    setActiveDrag(null);
  };


  const openAddDialog = (dateKey, prefillTime = "") => {
    setAddDialogDate(dateKey);
    setNewDueTime(prefillTime);
  };

  const handleCreateForDay = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      const data = await createTask({
        title: newTitle.trim(),
        due_date: addDialogDate,
        due_time: newDueTime || null,
      });
      setTasks((prev) => [data, ...prev]);
      setNewTitle("");
      setNewDueTime("");
      setAddDialogDate(null);
    } catch (e) {
      toast.error(t("tasks.createFailedError"));
    }
  };

  const goPrev = () => {
    if (viewMode === "month") setAnchorDate((d) => subMonths(d, 1));
    else if (viewMode === "week") setAnchorDate((d) => subWeeks(d, 1));
    else setAnchorDate((d) => subDays(d, 1));
  };

  const goNext = () => {
    if (viewMode === "month") setAnchorDate((d) => addMonths(d, 1));
    else if (viewMode === "week") setAnchorDate((d) => addWeeks(d, 1));
    else setAnchorDate((d) => addDaysFn(d, 1));
  };

  const weekdayLabels = t("schedule.weekdays", { returnObjects: true });

  const headerTitle = useMemo(() => {
    if (viewMode === "month") return format(anchorDate, "yyyy-MM");
    if (viewMode === "week") {
      const start = startOfWeek(anchorDate, { weekStartsOn: 1 });
      const end = endOfWeek(anchorDate, { weekStartsOn: 1 });
      return `${format(start, "MM-dd")} ~ ${format(end, "MM-dd")}`;
    }
    return `${format(anchorDate, "yyyy-MM-dd")} ${weekdayLabels[getDay(anchorDate)]}`;
  }, [anchorDate, viewMode, weekdayLabels]);

  const dayCellPadding = viewMode === "week" ? "p-1.5 gap-1" : "p-1 gap-0.5";
  const dayCellTaskGap = viewMode === "week" ? "space-y-1" : "space-y-0.5";

  // Month view's `days` is always full weeks (see the `days` useMemo above
  // using startOfWeek/endOfWeek), so chunking it into rows of 7 gives every
  // month's actual week count (4, 5 or 6) - each row is then rendered with
  // flex-1 so together they always fill the exact available height with no
  // leftover blank space and no page scrollbar, whatever that count is.
  const monthWeekRows = useMemo(() => {
    if (viewMode !== "month") return [];
    const rows = [];
    for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
    return rows;
  }, [days, viewMode]);

  // Full-detail task row shared by: day-view all-day section, the mobile
  // month-view day sheet, and the mobile week-view stacked day list.
  // `dragContext` turns on mobile long-press-drag ("timeline" = drag onto
  // the day-view hour grid to set/change due_time, "grid" = drag onto
  // another day's section to change due_date); omitted entirely for the
  // month-view day sheet, which has no visible drop target behind it.
  // Tapping (no drag) always opens TaskDetailDialog same as before.
  const renderTaskListRow = (task, dragContext) => {
    const hasDrag = isMobile && dragContext;
    const dragging = activeDrag?.taskId === task.id;
    return (
      <div
        key={task.id}
        data-testid={`calendar-task-chip-${task.id}`}
        onClick={!hasDrag ? () => setDetailTask(task) : undefined}
        onPointerDown={hasDrag ? (e) => handleChipPointerDown(e, task, dragContext) : undefined}
        onPointerMove={hasDrag ? handleChipPointerMove : undefined}
        onPointerUp={hasDrag ? (e) => handleChipPointerUp(e, task, dragContext, { onTap: () => setDetailTask(task) }) : undefined}
        onPointerCancel={hasDrag ? handleChipPointerCancel : undefined}
        style={{ touchAction: hasDrag ? "none" : undefined, WebkitTouchCallout: hasDrag ? "none" : undefined, ...(dragging ? dragStyleFor(task.id) : {}) }}
        className={`relative flex items-center gap-3 border bg-card px-3 py-2.5 transition-colors ${hasDrag ? "select-none" : "cursor-pointer hover:bg-secondary/40"} ${dragging ? "shadow-lg" : ""} ${overdueTasks.some((od) => od.id === task.id) ? "border-red-500/50" : "border-border"}`}
      >
        {dragging && dragContext === "timeline" && activeDrag.previewLabel && (
          <span data-testid="drag-time-preview" className="absolute -top-6 left-0 bg-primary text-primary-foreground text-[10px] font-mono-tag font-bold px-1.5 py-0.5 whitespace-nowrap z-10">
            {activeDrag.previewLabel}
          </span>
        )}
        <Checkbox
          checked={task.completed}
          onCheckedChange={() => handleToggleComplete(task)}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        />
        <span className={`flex-1 text-sm md:text-base font-bold truncate ${task.completed ? "line-through text-muted-foreground" : ""}`}>{task.due_time ? `${task.due_time} ` : ""}{task.title}</span>
      </div>
    );
  };

  // A day's full task list + "add" button, used both inside the mobile
  // month-view bottom sheet (draggable=false - the sheet overlays the grid,
  // so there's nothing visible underneath to drop a task onto) and stacked
  // per-day in the mobile week view (draggable=true - rows can be
  // long-press-dragged down/up onto a different day's section header).
  const renderDayTaskSection = (day, { draggable = false } = {}) => {
    const dateKey = format(day, "yyyy-MM-dd");
    const dayTasks = tasksByDate[dateKey] || [];
    const isDropTarget = draggable && activeDrag?.context === "grid" && activeDrag.overDateKey === dateKey;
    return (
      <div key={dateKey} data-testid={`calendar-day-${dateKey}`} data-drop-date={dateKey} className={`space-y-2 transition-colors ${isDropTarget ? "ring-2 ring-primary/50 bg-primary/5" : ""}`}>
        <div className="flex items-center justify-between">
          <span className={`text-sm font-bold flex items-center gap-2 ${isToday(day) ? "text-primary" : ""}`}>
            {weekdayLabels[getDay(day)]} {format(day, "MM-dd")}
            {isToday(day) && <span className="font-mono-tag font-bold text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5">{t("schedule.today")}</span>}
          </span>
          <button data-testid={`calendar-add-task-${dateKey}`} onClick={() => openAddDialog(dateKey)} className="h-8 w-8 flex items-center justify-center text-primary hover:bg-secondary transition-colors">
            <Plus size={16} weight="bold" />
          </button>
        </div>
        {dayTasks.length === 0 ? (
          <p className="text-xs font-bold text-muted-foreground py-2">{t("schedule.dayViewEmpty")}</p>
        ) : (
          <div className="space-y-1.5">{dayTasks.map((task) => renderTaskListRow(task, draggable ? "grid" : undefined))}</div>
        )}
      </div>
    );
  };

  // Desktop month/week grid cell: mouse-draggable task chips + drop target.
  const renderGridCell = (day) => {
    const dateKey = format(day, "yyyy-MM-dd");
    const dayTasks = tasksByDate[dateKey] || [];
    const inMonth = viewMode !== "month" || isSameMonth(day, anchorDate);
    const hasOverdue = dayTasks.some((tk) => overdueTasks.some((od) => od.id === tk.id));
    const isDragOver = dragOverDate === dateKey;
    return (
      <div
        key={dateKey}
        data-testid={`calendar-day-${dateKey}`}
        onDragOver={(e) => { e.preventDefault(); setDragOverDate(dateKey); }}
        onDragLeave={() => setDragOverDate((prev) => (prev === dateKey ? null : prev))}
        onDrop={(e) => { e.preventDefault(); handleDropTask(e.dataTransfer.getData("text/plain"), dateKey); }}
        className={`border-r border-b h-full min-h-0 ${dayCellPadding} flex flex-col transition-colors ${hasOverdue ? "border-red-500/50" : "border-border"} ${isDragOver ? "bg-primary/10" : inMonth ? "bg-card" : "bg-muted/40"}`}
      >
        <div className="flex items-center justify-between shrink-0">
          <span className={`text-sm font-bold ${!inMonth ? "text-muted-foreground" : ""} ${isToday(day) ? "bg-primary text-primary-foreground px-1.5 py-0.5" : ""}`}>
            {format(day, "d")}
          </span>
          <button
            data-testid={`calendar-add-task-${dateKey}`}
            onClick={() => openAddDialog(dateKey)}
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            <Plus size={14} weight="bold" />
          </button>
        </div>
        <div className={`${dayCellTaskGap} overflow-y-auto flex-1 min-h-0`}>
          {dayTasks.map((task) => (
            <div
              key={task.id}
              data-testid={`calendar-task-chip-${task.id}`}
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/plain", task.id)}
              onClick={() => setDetailTask(task)}
              className="flex items-center gap-1 text-xs font-bold bg-secondary px-1 py-[1px] cursor-grab hover:ring-1 hover:ring-primary/40 transition-shadow"
            >
              <Checkbox
                data-testid={`calendar-task-checkbox-${task.id}`}
                checked={task.completed}
                onCheckedChange={() => handleToggleComplete(task)}
                onClick={(e) => e.stopPropagation()}
                className="h-3.5 w-3.5"
              />
              <span className={`truncate ${task.completed ? "line-through text-muted-foreground" : ""}`}>{task.due_time ? `${task.due_time} ` : ""}{task.title}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Mobile month-view cell: bigger tap target (whole cell), no tiny "+" icon
  // or illegible mini task chips - just the day number plus up to
  // MAX_MOBILE_DOTS task dots. Tapping opens a bottom sheet with
  // the full task list + add button for that day. Each dot can also be
  // long-press-dragged onto another day cell to reschedule that one task
  // (the grid itself shows no individual task rows, so the dot is the only
  // drag handle available here) - a plain tap on a dot still falls through
  // to the button's own onClick and opens the day sheet as before.
  const renderMobileMonthCell = (day) => {
    const dateKey = format(day, "yyyy-MM-dd");
    const dayTasks = tasksByDate[dateKey] || [];
    const inMonth = isSameMonth(day, anchorDate);
    const hasOverdue = dayTasks.some((tk) => overdueTasks.some((od) => od.id === tk.id));
    const visibleDots = dayTasks.slice(0, MAX_MOBILE_DOTS);
    const isDropTarget = activeDrag?.context === "grid" && activeDrag.overDateKey === dateKey;
    return (
      <button
        key={dateKey}
        data-testid={`calendar-day-${dateKey}`}
        data-drop-date={dateKey}
        onClick={() => { if (justDraggedRef.current) return; setDaySheetDate(dateKey); }}
        className={`border-r border-b min-h-[52px] p-1 flex flex-col items-center gap-1 transition-colors ${hasOverdue ? "border-red-500/50" : "border-border"} ${isDropTarget ? "bg-primary/20 ring-2 ring-primary/50" : inMonth ? "bg-card active:bg-secondary" : "bg-muted/40"}`}
      >
        <span className={`text-xs font-bold ${!inMonth ? "text-muted-foreground" : ""} ${isToday(day) ? "h-5 w-5 flex items-center justify-center rounded-full bg-primary text-primary-foreground" : ""}`}>
          {format(day, "d")}
        </span>
        {dayTasks.length > 0 && (
          <div className="flex items-center gap-0.5" data-testid={`calendar-month-dots-${dateKey}`}>
            {visibleDots.map((task) => {
              const dragging = activeDrag?.taskId === task.id;
              return (
                <span
                  key={task.id}
                  data-testid={`calendar-task-dot-${task.id}`}
                  onPointerDown={(e) => { e.stopPropagation(); handleChipPointerDown(e, task, "grid"); }}
                  onPointerMove={handleChipPointerMove}
                  onPointerUp={(e) => { e.stopPropagation(); handleChipPointerUp(e, task, "grid"); }}
                  onPointerCancel={handleChipPointerCancel}
                  style={{ touchAction: "none", WebkitTouchCallout: "none", ...dragStyleFor(task.id) }}
                  className={`h-3 w-3 -m-0.5 flex items-center justify-center select-none ${dragging ? "z-50" : ""}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full bg-muted-foreground ${dragging ? "scale-150 bg-primary" : ""}`} />
                </span>
              );
            })}
            {dayTasks.length > MAX_MOBILE_DOTS && <span className="text-[9px] font-bold text-muted-foreground leading-none">+{dayTasks.length - MAX_MOBILE_DOTS}</span>}
          </div>
        )}
      </button>
    );
  };

  const renderDayTimeline = () => {
    const dateKey = format(anchorDate, "yyyy-MM-dd");
    const dayTasks = tasksByDate[dateKey] || [];
    const allDayTasks = dayTasks.filter((tk) => !tk.due_time);
    const timedTasks = dayTasks.filter((tk) => tk.due_time);
    const now = new Date();
    // HOURS now starts at 8 (not midnight), so every offset needs to be
    // relative to the grid's first row rather than 00:00.
    const gridStartMinutes = HOURS[0] * 60;
    const showNowLine = isToday(anchorDate) && now.getHours() >= HOURS[0] && now.getHours() <= HOURS[HOURS.length - 1];
    const nowOffset = (now.getHours() * 60 + now.getMinutes() - gridStartMinutes) * (hourHeight / 60);
    const timeToOffset = (dueTime) => {
      const [h, m] = dueTime.split(":").map(Number);
      return (h * 60 + m - gridStartMinutes) * (hourHeight / 60);
    };
    const hourLabelWidth = isMobile ? "w-11" : "w-14";

    return (
      <div className={`w-full ${isMobile ? "" : "h-full flex flex-col"}`} data-testid={`calendar-day-${dateKey}`}>
        {allDayTasks.length > 0 && (
          <div className="mb-4 space-y-1 shrink-0" data-testid="day-view-allday-section">
            <p className="text-xs md:text-sm font-mono-tag font-bold text-muted-foreground">{t("schedule.allDay")}</p>
            {allDayTasks.map((task) => renderTaskListRow(task, "timeline"))}
          </div>
        )}

        {!isMobile && (
          <p className="text-xs md:text-sm font-bold text-muted-foreground mb-2 shrink-0">{t("schedule.dragHintDay")}</p>
        )}
        <div
          ref={timelineRef}
          className={`relative border-t border-l border-border ${isMobile ? "overflow-y-auto max-h-[65vh]" : "flex-1 min-h-0 overflow-hidden"}`}
          data-testid="day-view-timeline"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleTimelineDrop}
        >
          {HOURS.map((h) => (
            <div key={h} className="flex border-r border-b border-border" style={{ height: `${hourHeight}px` }}>
              <div className={`${hourLabelWidth} shrink-0 border-r border-border text-[10px] md:text-xs text-muted-foreground font-mono-tag font-bold pt-1 pr-1.5 text-right`}>{pad2(h)}:00</div>
              <div
                data-testid={`day-view-hour-slot-${h}`}
                onClick={() => openAddDialog(dateKey, `${pad2(h)}:00`)}
                className="flex-1 hover:bg-secondary/30 active:bg-secondary/50 cursor-pointer transition-colors"
              />
            </div>
          ))}

          {showNowLine && (
            <div className={`absolute ${isMobile ? "left-11" : "left-14"} right-0 border-t-2 border-red-500 z-10 pointer-events-none`} data-testid="day-view-now-line" style={{ top: `${nowOffset}px` }}>
              <span className="absolute -left-1 -top-[5px] h-[9px] w-[9px] rounded-full bg-red-500" />
            </div>
          )}

          {timedTasks.map((task) => {
            const isOverdue = overdueTasks.some((od) => od.id === task.id);
            const hasDrag = isMobile;
            const dragging = activeDrag?.taskId === task.id;
            return (
              <div
                key={task.id}
                data-testid={`calendar-task-chip-${task.id}`}
                draggable={!isMobile}
                onDragStart={(e) => e.dataTransfer.setData("text/plain", task.id)}
                onClick={!hasDrag ? () => setDetailTask(task) : undefined}
                onPointerDown={hasDrag ? (e) => handleChipPointerDown(e, task, "timeline") : undefined}
                onPointerMove={hasDrag ? handleChipPointerMove : undefined}
                onPointerUp={hasDrag ? (e) => handleChipPointerUp(e, task, "timeline", { onTap: () => setDetailTask(task) }) : undefined}
                onPointerCancel={hasDrag ? handleChipPointerCancel : undefined}
                className={`absolute ${isMobile ? "left-11" : "left-14"} right-2 ml-1 px-2 py-1 text-xs md:text-sm font-bold border z-[5] bg-card border-border transition-shadow ${isMobile ? "select-none" : "cursor-grab hover:ring-1 hover:ring-primary/40"} ${isOverdue ? "ring-1 ring-red-500" : ""} ${dragging ? "shadow-lg" : "shadow-sm"}`}
                style={{ top: `${timeToOffset(task.due_time)}px`, minHeight: "28px", touchAction: hasDrag ? "none" : undefined, WebkitTouchCallout: hasDrag ? "none" : undefined, ...(dragging ? dragStyleFor(task.id) : {}) }}
              >
                {dragging && activeDrag.previewLabel && (
                  <span data-testid="drag-time-preview" className="absolute -top-6 left-0 bg-primary text-primary-foreground text-[10px] font-mono-tag font-bold px-1.5 py-0.5 whitespace-nowrap z-10">
                    {activeDrag.previewLabel}
                  </span>
                )}
                <span className="font-mono-tag mr-1">{task.due_time}</span>
                <span className={task.completed ? "line-through opacity-60" : ""}>{task.title}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Desktop week view side panel: unscheduled tasks the user can drag
  // directly onto a day cell to assign a due_date (see handleDropTask).
  const renderUnscheduledPanel = () => (
    <div className="border border-border bg-card p-3 space-y-3" data-testid="schedule-unscheduled-panel">
      <p className="text-xs md:text-sm font-mono-tag font-bold text-muted-foreground">{t("schedule.unscheduledTasksTitle")}</p>
      <form onSubmit={handleQuickAddUnscheduled} className="flex gap-1.5">
        <Input
          data-testid="schedule-unscheduled-add-input"
          value={newUnscheduledTitle}
          onChange={(e) => setNewUnscheduledTitle(e.target.value)}
          placeholder={t("tasks.newTaskPlaceholder")}
          className="rounded-none flex-1 text-sm font-bold h-9"
        />
        <Button type="submit" data-testid="schedule-unscheduled-add-button" size="icon" className="rounded-none h-9 w-9 shrink-0">
          <Plus size={14} weight="bold" />
        </Button>
      </form>
      {unscheduledTasks.length === 0 ? (
        <p className="text-xs font-bold text-muted-foreground py-4 text-center">{t("schedule.unscheduledEmpty")}</p>
      ) : (
        <div className="space-y-1.5 max-h-[520px] overflow-y-auto" data-testid="schedule-unscheduled-list">
          {unscheduledTasks.map((task) => (
            <div
              key={task.id}
              data-testid={`schedule-unscheduled-task-${task.id}`}
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/plain", task.id)}
              onClick={() => setDetailTask(task)}
              className="flex items-center gap-2 border border-border bg-background px-2 py-2 text-xs md:text-sm font-bold cursor-grab hover:ring-1 hover:ring-primary/40 transition-shadow"
            >
              <DotsSixVertical size={14} className="text-muted-foreground shrink-0" />
              <span className="truncate">{task.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="schedule-page">
      <ModuleNavBar active="schedule" />
      <div className={`flex-1 p-2.5 md:p-4 flex flex-col ${isMobile ? "overflow-y-auto" : "overflow-hidden min-h-0"}`}>
        <div className="flex items-center justify-between mb-2 md:mb-3 flex-wrap gap-2 shrink-0">
          <h1 className="font-display text-lg md:text-2xl font-black tracking-tight flex items-center gap-2">
            <CalendarBlank size={20} className="md:hidden" />
            <CalendarBlank size={22} className="hidden md:block" />
            {headerTitle}
            {reminderCount > 0 && (
              <span data-testid="schedule-reminder-badge" className="flex items-center gap-1 text-[11px] font-mono-tag text-red-600 border border-red-500/40 bg-red-500/10 px-2 py-0.5 rounded-full">
                <BellRinging size={12} weight="fill" /> {t("reminders.reminderCount", { count: reminderCount })}
              </span>
            )}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Button data-testid="calendar-prev-button" variant="outline" size="icon" className="rounded-none h-8 w-8" onClick={goPrev}>
                <CaretLeft size={14} />
              </Button>
              <Button data-testid="calendar-today-button" variant="outline" className="rounded-none font-bold text-sm h-8 px-3" onClick={() => setAnchorDate(new Date())}>
                {t("schedule.today")}
              </Button>
              <Button data-testid="calendar-next-button" variant="outline" size="icon" className="rounded-none h-8 w-8" onClick={goNext}>
                <CaretRight size={14} />
              </Button>
            </div>
            <div className="flex items-center border border-border w-fit">
              {VIEW_MODES.map((mode) => (
                <button
                  key={mode}
                  data-testid={`schedule-view-${mode}`}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 text-sm font-bold transition-colors ${
                    viewMode === mode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
                >
                  {t(`schedule.view${mode.charAt(0).toUpperCase() + mode.slice(1)}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {isMobile ? (
          viewMode === "day" ? (
            renderDayTimeline()
          ) : viewMode === "month" ? (
            <div>
              <p className="text-xs font-bold text-muted-foreground mb-2">{t("schedule.tapHintMobile")}</p>
              <div className="grid grid-cols-7 border-t border-l border-border">
                {weekdayLabels.map((label) => (
                  <div key={label} className="border-r border-b border-border bg-secondary px-1 py-1.5 text-xs font-mono-tag font-bold text-center">{label}</div>
                ))}
                {days.map(renderMobileMonthCell)}
              </div>
            </div>
          ) : (
            <div className="space-y-4 divide-y divide-border">
              {days.map((day) => (
                <div key={format(day, "yyyy-MM-dd")} className="pt-4 first:pt-0">
                  {renderDayTaskSection(day, { draggable: true })}
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="flex-1 min-h-0">
            {viewMode === "day" ? (
              // Three columns per user request (2026-09-11): the day
              // timeline, then the Global Note immediately next to it, then
              // "我的收藏"+"钉钉表格链接" stacked in one column on the far
              // right (mirrors the week view's stacked unscheduled/overdue
              // panel). `items-stretch` (the default, so no "items-start"
              // override) makes every column match the tallest one's height.
              <div className="max-w-[1600px] w-full h-full flex gap-4">
                <div className="flex-1 min-w-0">{renderDayTimeline()}</div>
                <div className="flex-[2] min-w-0">
                  <GlobalNoteBox className="h-full" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-4">
                  <FavoriteNotesCard onOpenCapture={handleOpenFavoriteCapture} refreshKey={favoritesRefreshKey} />
                  <DingtalkLinksCard className="flex-1 flex flex-col" />
                </div>
              </div>
            ) : (
              <div className="flex gap-3 h-full">
                <div className={`h-full min-w-0 flex flex-col ${viewMode === "month" ? "flex-[3]" : "flex-1"}`}>
                  <p className="text-xs font-bold text-muted-foreground mb-1 shrink-0">{viewMode === "week" ? t("schedule.dragHintWeek") : t("schedule.dragHint")}</p>
                  {viewMode === "week" ? (
                    // Two rows (Mon-Thu, Fri-Sun) per user request, rather than
                    // one cramped 7-column row - `days` is already Monday-first
                    // (see the `days` useMemo above using weekStartsOn: 1). Each
                    // row is flex-1 so the two rows together always fill the
                    // exact available height with no scrollbar (2026-09-12).
                    <div className="flex-1 min-h-0 flex flex-col gap-3">
                      {[days.slice(0, 4), days.slice(4, 7)].map((rowDays, idx) => (
                        <div key={idx} className="flex-1 min-h-0 flex flex-col border-t border-l border-border">
                          <div className={`grid shrink-0 ${idx === 0 ? "grid-cols-4" : "grid-cols-3"}`}>
                            {rowDays.map((day) => (
                              <div key={`h-${format(day, "yyyy-MM-dd")}`} className="border-r border-b border-border bg-secondary px-2 py-1.5 text-xs md:text-sm font-mono-tag font-bold text-center">
                                {weekdayLabels[getDay(day)]}
                              </div>
                            ))}
                          </div>
                          <div className={`flex-1 min-h-0 grid ${idx === 0 ? "grid-cols-4" : "grid-cols-3"}`}>
                            {rowDays.map(renderGridCell)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Month rows are chunked via `monthWeekRows` (4/5/6 weeks
                    // depending on the month) and each rendered with flex-1 so
                    // together they always fill the exact remaining height
                    // with zero blank space and no page scrollbar (2026-09-12).
                    <div className="flex-1 min-h-0 flex flex-col border-t border-l border-border">
                      <div className="grid grid-cols-7 shrink-0">
                        {weekdayLabels.map((label) => (
                          <div key={label} className="border-r border-b border-border bg-secondary px-2 py-1 text-xs md:text-sm font-mono-tag font-bold text-center">{label}</div>
                        ))}
                      </div>
                      <div className="flex-1 min-h-0 flex flex-col">
                        {monthWeekRows.map((weekDays, idx) => (
                          <div key={idx} className="flex-1 min-h-0 grid grid-cols-7">
                            {weekDays.map(renderGridCell)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* Global Note sidebar, right of the grid, per user request -
                    same account-wide note shown on the Tasks page / day view.
                    Month view uses a ~75/25 flex ratio with the grid (per user
                    request); week view keeps its original fixed w-80 unchanged. */}
                <div className={`h-full self-stretch ${viewMode === "month" ? "flex-1 min-w-0" : "w-80 shrink-0"}`} data-testid="schedule-global-note-panel">
                  <GlobalNoteBox className="h-full" />
                </div>
                {/* Unscheduled + overdue tasks moved to the far right per user
                    request (2026-09-11) - was previously left of the grid. */}
                {viewMode === "week" && (
                  <div className="w-64 shrink-0 h-full flex flex-col gap-4">
                    {renderUnscheduledPanel()}
                    <OverdueTasksCard overdueTasks={overdueTasks} onToggle={handleToggleComplete} onOpenDetail={setDetailTask} previewSize={14} className="flex-1 flex flex-col" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Sheet open={!!daySheetDate} onOpenChange={(o) => !o && setDaySheetDate(null)}>
        <SheetContent side="bottom" className="rounded-none max-h-[75vh] overflow-y-auto" data-testid="mobile-day-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">{daySheetDate}</SheetTitle>
            <SheetDescription>{t("schedule.addTaskDescription")}</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {daySheetDate && renderDayTaskSection(new Date(`${daySheetDate}T00:00:00`))}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={!!addDialogDate} onOpenChange={(o) => { if (!o) { setAddDialogDate(null); setNewTitle(""); setNewDueTime(""); } }}>
        <DialogContent className="rounded-none max-w-sm" data-testid="calendar-add-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t("schedule.addTaskTitle", { date: addDialogDate })}</DialogTitle>
            <DialogDescription>{t("schedule.addTaskDescription")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateForDay} className="space-y-4">
            <Input
              data-testid="calendar-add-task-input"
              autoFocus
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={t("tasks.newTaskPlaceholder")}
              className="rounded-none font-bold text-sm md:text-base"
            />
            <Input
              data-testid="calendar-add-task-time-input"
              type="time"
              value={newDueTime}
              onChange={(e) => setNewDueTime(e.target.value)}
              className="rounded-none w-32 font-bold text-sm md:text-base"
            />
            <Button type="submit" data-testid="calendar-add-task-submit-button" className="w-full rounded-none font-bold text-sm md:text-base">
              {t("tasks.addTask")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <TaskDetailDialog
        task={detailTask}
        open={!!detailTask}
        onOpenChange={(o) => !o && setDetailTask(null)}
        onSaved={(updated) => setTasks((prev) => prev.map((tk) => (tk.id === updated.id ? updated : tk)))}
        onDeleted={(id) => setTasks((prev) => prev.filter((tk) => tk.id !== id))}
      />

      <CaptureEditDialog
        capture={viewingCapture}
        open={!!viewingCapture}
        onOpenChange={handleViewingCaptureOpenChange}
        onDelete={handleViewingCaptureDeleted}
        onSaved={setViewingCapture}
      />
    </div>
  );
}
